import styles from './MaterialPreviewModal.module.css';
import { ImageEditCanvas } from './editing/ImageEditCanvas';
import { ImageEditSidebar } from './editing/ImageEditSidebar';
import type { CropState, AspectRatio } from './editing/ImageEditCanvas';
import type { EditState } from './types';
import { MIN_ZOOM, MAX_ZOOM } from './types';

interface MaterialEditViewProps {
  displayPreviewUrl: string | null;
  selectedAspectRatio: AspectRatio;
  editState: EditState;
  cropState: CropState;
  zoom: number;
  zoomDisplay: string;
  platformSelectOptions: Array<{ id: string; name: string }>;
  menuSelectOptions: Array<{ id: string; label: string; platformId: string; description?: string | null }>;
  specSelectOptions: Array<{
    id: string;
    label: string;
    description?: string | null;
    origin?: 'global' | 'custom';
    guidelines: string[];
    menuGuidelines: string[];
    sizeSummary?: string;
    allowedAspectRatios?: AspectRatio[];
  }>;
  selectedPlatformId: string | null;
  selectedMenuId: string | null;
  selectedSpecId: string | null;
  platformLoading: boolean;
  menuLoading: boolean;
  specLoading: boolean;
  menuGuidelinesState: string[];
  specGuidelinesState: string[];
  mediaResolutionPresets: Record<AspectRatio, Array<{ id: string; label: string; width: number; height: number }>>;
  allowedAspectRatiosForSidebar: AspectRatio[] | null;
  autoFitRef: React.MutableRefObject<boolean>;
  lastPreviewPayloadRef: React.MutableRefObject<any>;
  viewportRef: React.MutableRefObject<HTMLDivElement | null>;
  onZoomStep: (direction: number) => void;
  onCanvasInteractionStart: () => void;
  onCanvasInteractionEnd: () => void;
  onImageLoad: (width: number, height: number) => void;
  onCropAspectRatioChange: (ratio: AspectRatio) => void;
  onSelectPlatform: (platformId: string | null) => void;
  onSelectMenu: (menuId: string | null) => void;
  onSelectSpec: (specId: string | null) => void;
  setCropState: React.Dispatch<React.SetStateAction<CropState>>;
  setEditState: React.Dispatch<React.SetStateAction<EditState>>;
  setPreviewJobId: (jobId: string | null) => void;
  setIsTransformApplied: (applied: boolean) => void;
  cropEquals: (a: CropState, b: CropState) => boolean;
  // スマホ用: キャンバス下のボタン
  onReset?: () => void;
  onApplyTransform?: () => void;
  isSubmitting?: boolean;
  isPreviewLoading?: boolean;
  /** 変更があるかどうか（リセット・適用ボタン表示用） */
  hasChanges?: boolean;
  /** スマホ表示時の最大幅（キャンバス表示サイズ制限用） */
  mobileMaxWidth?: number;
  /** スマホ表示時の最大高さ（キャンバス表示サイズ制限用） */
  mobileMaxHeight?: number;
}

export function MaterialEditView({
  displayPreviewUrl,
  selectedAspectRatio,
  editState,
  cropState,
  zoom,
  zoomDisplay,
  platformSelectOptions,
  menuSelectOptions,
  specSelectOptions,
  selectedPlatformId,
  selectedMenuId,
  selectedSpecId,
  platformLoading,
  menuLoading,
  specLoading,
  menuGuidelinesState,
  specGuidelinesState,
  mediaResolutionPresets,
  allowedAspectRatiosForSidebar,
  autoFitRef,
  lastPreviewPayloadRef,
  viewportRef,
  onZoomStep,
  onCanvasInteractionStart,
  onCanvasInteractionEnd,
  onImageLoad,
  onCropAspectRatioChange,
  onSelectPlatform,
  onSelectMenu,
  onSelectSpec,
  setCropState,
  setEditState,
  setPreviewJobId,
  setIsTransformApplied,
  cropEquals,
  onReset,
  onApplyTransform,
  isSubmitting,
  isPreviewLoading,
  hasChanges,
  mobileMaxWidth,
  mobileMaxHeight,
}: MaterialEditViewProps) {

  return (
    <div className={styles.editLayout}>
      <div className={styles.canvasPane}>
        <div className={styles.canvasViewport} ref={viewportRef}>
          {displayPreviewUrl ? (
            <ImageEditCanvas
              key={displayPreviewUrl}
              imageUrl={displayPreviewUrl}
              aspectRatio={selectedAspectRatio}
              paddingMode={editState.padding}
              crop={cropState}
              zoom={zoom}
              onZoomStep={onZoomStep}
              onInteractionStart={onCanvasInteractionStart}
              onInteractionEnd={onCanvasInteractionEnd}
              onUpdateCrop={(nextCrop) => {
                setCropState((prev) => {
                  if (cropEquals(prev, nextCrop)) {
                    return prev;
                  }
                  setIsTransformApplied(false);
                  return nextCrop;
                });
                setPreviewJobId(null);
                lastPreviewPayloadRef.current = null;
              }}
              onImageLoad={onImageLoad}
              maxDisplayWidth={mobileMaxWidth}
              maxDisplayHeight={mobileMaxHeight}
            />
          ) : (
            <div className={styles.canvasPlaceholder}>
              <span>プレビュー画像がありません</span>
            </div>
          )}
        </div>
        <div className={styles.zoomControls}>
          <button
            type="button"
            className={styles.zoomButton}
            onClick={() => onZoomStep(-1)}
            disabled={zoom <= MIN_ZOOM + 0.0001}
            aria-label="ズームアウト"
          >
            −
          </button>
          <span className={styles.zoomValue}>{zoomDisplay}</span>
          <button
            type="button"
            className={styles.zoomButton}
            onClick={() => onZoomStep(1)}
            disabled={zoom >= MAX_ZOOM - 0.0001}
            aria-label="ズームイン"
          >
            ＋
          </button>
        </div>
      </div>
      {/* スマホ用: キャンバス下のボタン（canvasPaneの外、変更がある場合のみ表示） */}
      {onReset && onApplyTransform && hasChanges && (
        <div className={styles.canvasActionsMobile}>
          <button
            type="button"
            className={`${styles.canvasActionButton} ${styles.canvasActionButtonDanger}`}
            onClick={onReset}
            disabled={isSubmitting || isPreviewLoading}
          >
            <img src="/delete_2.svg" alt="" width={16} height={16} />
            リセット
          </button>
          <button
            type="button"
            className={styles.canvasActionButton}
            onClick={onApplyTransform}
            disabled={isSubmitting || isPreviewLoading}
          >
            <img src="/crop.svg" alt="" width={16} height={16} />
            切り抜きを適用
          </button>
        </div>
      )}
      <ImageEditSidebar
        width={editState.width}
        height={editState.height}
        selectedResolutionId={editState.selectedResolutionId}
        cropAspectRatio={selectedAspectRatio}
        padding={editState.padding}
        format={editState.format}
        quality={editState.quality}
        onCropAspectRatioChange={onCropAspectRatioChange}
        onChange={(next) => {
          setEditState((prev) => ({ ...prev, ...next }));
          if (next.width !== undefined || next.height !== undefined) {
            autoFitRef.current = true;
          }
          if (
            next.width !== undefined ||
            next.height !== undefined ||
            next.selectedResolutionId !== undefined ||
            next.padding !== undefined ||
            next.format !== undefined ||
            next.quality !== undefined
          ) {
            setPreviewJobId(null);
            lastPreviewPayloadRef.current = null;
            setIsTransformApplied(false);
          }
        }}
        platformOptions={platformSelectOptions}
        menuOptions={menuSelectOptions}
        specOptions={specSelectOptions}
        selectedPlatformId={selectedPlatformId}
        selectedMenuId={selectedMenuId}
        selectedSpecId={selectedSpecId}
        onSelectPlatform={onSelectPlatform}
        onSelectMenu={onSelectMenu}
        onSelectSpec={onSelectSpec}
        platformLoading={platformLoading}
        menuLoading={menuLoading}
        specLoading={specLoading}
        menuGuidelines={menuGuidelinesState}
        guidelines={specGuidelinesState}
        resolutionPresets={mediaResolutionPresets}
        allowedAspectRatios={allowedAspectRatiosForSidebar}
      />
    </div>
  );
}
